declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
    afIsAnimationsEnabled?: () => boolean;
    afToggleAnimations?: (enable?: boolean) => boolean;
  }
}

export function pushThemeGtmEvent(event: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  try {
    const w = window as unknown as { dataLayer?: unknown[] };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ event, ...params });
  } catch {}
}

export function getAntiforgeryToken(form?: HTMLFormElement | null): string {
  if (typeof document === 'undefined') return '';
  if (form) {
    const formInput = form.querySelector(
      'input[name="__RequestVerificationToken"]'
    ) as HTMLInputElement;
    if (formInput?.value) return formInput.value;
  }
  const input = document.querySelector(
    'input[name="__RequestVerificationToken"]'
  ) as HTMLInputElement;
  if (input?.value) return input.value;
  const metaEl = document.querySelector('meta[name="antiforgery-token"]');
  const metaToken = metaEl?.getAttribute('content');
  if (metaToken) return metaToken;
  const rootEl = document.getElementById('root');
  const rootToken = rootEl?.getAttribute('data-antiforgery-token');
  if (rootToken) return rootToken;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, val] = cookie.trim().split('=');
    if (name === 'XSRF-TOKEN' || name === 'RequestVerificationToken') {
      return decodeURIComponent(val || '');
    }
  }
  return '';
}

let recaptchaScriptPromise: Promise<void> | null = null;

export function loadRecaptchaScript(siteKey: string): Promise<void> {
  if (typeof window === 'undefined' || !siteKey) return Promise.resolve();
  if (window.grecaptcha) return Promise.resolve();
  if (recaptchaScriptPromise) return recaptchaScriptPromise;

  recaptchaScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[src*="recaptcha/api.js"]');
    if (existing) {
      if (window.grecaptcha) {
        resolve();
      } else {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', (err) => reject(err));
      }
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = (err) => {
      console.warn('Failed to load Google reCAPTCHA script:', err);
      reject(err);
    };
    document.head.appendChild(script);
  });

  return recaptchaScriptPromise;
}

export function getRecaptchaTokenForForm(
  form: HTMLFormElement,
  action = 'submit'
): Promise<string | null> {
  const isEnabled =
    form.dataset.captchaEnabled === 'true' || form.getAttribute('data-captcha-enabled') === 'true';
  const siteKey =
    form.dataset.recaptchaSiteKey || form.getAttribute('data-recaptcha-site-key') || '';

  if (!isEnabled || !siteKey) {
    return Promise.resolve(null);
  }

  const tokenPromise = new Promise<string | null>((resolve) => {
    loadRecaptchaScript(siteKey)
      .then(() => {
        if (!window.grecaptcha) {
          resolve(null);
          return;
        }
        window.grecaptcha.ready(() => {
          try {
            window
              .grecaptcha!.execute(siteKey, { action })
              .then((token) => resolve(token))
              .catch((err) => {
                console.warn('Google reCAPTCHA execute rejected:', err);
                resolve(null);
              });
          } catch (err) {
            console.warn('Google reCAPTCHA execute error:', err);
            resolve(null);
          }
        });
      })
      .catch((err) => {
        console.warn('Google reCAPTCHA script error:', err);
        resolve(null);
      });
  });

  const timeoutPromise = new Promise<string | null>((resolve) => {
    setTimeout(() => resolve(null), 5000);
  });

  return Promise.race([tokenPromise, timeoutPromise]);
}

// Animation System State & Control
export function isAnimationsEnabled(): boolean {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return true;
  try {
    const stored = localStorage.getItem('loumars-animations-enabled');
    if (stored !== null) return stored === 'true' || stored === '1';
  } catch {}
  return true; // Default ON
}

export function applyAnimationState(enabled: boolean): void {
  if (typeof document !== 'undefined') {
    document.documentElement.classList.toggle('animations-disabled', !enabled);
    if (document.body) {
      document.body.classList.toggle('animations-disabled', !enabled);
    }
  }
  try {
    localStorage.setItem('loumars-animations-enabled', enabled ? 'true' : 'false');
  } catch {}

  // If disabled or reduced motion, immediately mark all items as animated so content is visible
  if (
    !enabled ||
    (typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      Boolean(window.matchMedia('(prefers-reduced-motion: reduce)')?.matches))
  ) {
    if (typeof document !== 'undefined') {
      document.querySelectorAll<HTMLElement>('[data-animate="true"]').forEach(function (el) {
        el.setAttribute('data-animated', 'true');
      });
    }
  }

  try {
    window.dispatchEvent(
      new CustomEvent('loumars-animations-changed', { detail: { enabled: enabled } })
    );
  } catch {}
}

if (typeof window !== 'undefined') {
  window.afIsAnimationsEnabled = isAnimationsEnabled;
  window.afToggleAnimations = function (enable?: boolean): boolean {
    const nextState = typeof enable === 'boolean' ? enable : !isAnimationsEnabled();
    applyAnimationState(nextState);
    return nextState;
  };
}
